import { useState } from "react";
import { BillingPeriod } from "@/types/plans";
import { Badge } from "@/components/ui/badge";
import { PricePerDayDisplay } from "./PricePerDayDisplay";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { DensityMode } from "@/contexts/AdaptiveViewportContext";

interface PriceOption {
  period: BillingPeriod;
  price: number;
  originalPrice?: number;
  savings?: number;
}

interface PricingHeroCardProps {
  planId: string;
  planName: string;
  planTier: 'premium' | 'standard';
  topDifferentials: string[];
  priceOptions: PriceOption[];
  selectedOption: { planId: string; period: BillingPeriod } | null;
  onSelectOption: (planId: string, period: BillingPeriod) => void;
  isCurrent: boolean;
  densityMode?: DensityMode;
  isExpanded?: boolean;
}

const PERIOD_LABELS: Record<BillingPeriod, { full: string; short: string }> = {
  weekly: { full: "Semanal", short: "SEMANAL" },
  monthly: { full: "Mensal", short: "MENSAL" },
  quarterly: { full: "Trimestral", short: "TRIMESTRAL" },
};

export const PricingHeroCard = ({
  planId,
  planName,
  planTier,
  topDifferentials,
  priceOptions,
  selectedOption,
  onSelectOption,
  isCurrent,
  densityMode = 'expanded',
  isExpanded = true,
}: PricingHeroCardProps) => {
  // Mobile: NUNCA mostrar diferenciais (economizar espaço)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const visibleDifferentials = 
    isMobile || !isExpanded ? [] :  // Mobile ou colapsado = não mostra diferenciais
    densityMode === 'compact' ? [] : 
    densityMode === 'normal' ? topDifferentials.slice(0, 2) : 
    topDifferentials.slice(0, 3);
  const [hoveredPeriod, setHoveredPeriod] = useState<BillingPeriod | null>(null);

  // Preço por dia dinâmico baseado na seleção atual
  const getPricePerDay = () => {
    const selected = priceOptions.find(p => p.period === selectedOption?.period);
    if (!selected || selectedOption?.planId !== planId) return null;
    
    switch (selected.period) {
      case 'weekly': return selected.price / 7;
      case 'monthly': return selected.price / 30;
      case 'quarterly': return selected.price / 90;
    }
  };
  
  const pricePerDay = getPricePerDay();
  
  return (
    <div 
      className={cn(
        "relative rounded-2xl border-2 transition-all duration-300",
        planTier === 'premium' && [
          "bg-luxury-pinkBright/5 border-luxury-pinkBright/30",
          "shadow-[0_0_25px_rgba(233,30,99,0.3)]"
        ],
        planTier === 'standard' && [
          "bg-amber-500/5 border-amber-500/30",
          "shadow-[0_0_15px_rgba(245,158,11,0.2)]"
        ],
        densityMode === 'compact' ? "p-3" : "p-6"
      )}
    >
      {/* Glow effect */}
      <div className={cn(
        "absolute inset-0 rounded-2xl bg-gradient-to-b pointer-events-none",
        planTier === 'premium' ? "from-luxury-pinkBright/10" : "from-amber-500/10",
        "to-transparent"
      )} />
      
      {/* Header - compacto em mobile */}
      <div className="relative text-center mb-2 md:mb-4 pb-2 md:pb-4 border-b border-white/10">
        {planTier === 'premium' && (
          <Badge className="mb-1 text-[10px] md:text-xs bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30 font-semibold animate-pulse">
            ⭐ MAIS POPULAR
          </Badge>
        )}
        {planTier === 'standard' && (
          <Badge className="mb-1 text-[10px] md:text-xs bg-amber-500/20 text-amber-400 border-amber-500/30 font-semibold">
            💰 Melhor Custo-Benefício
          </Badge>
        )}
        <h3 className="text-base md:text-2xl font-bold text-white mb-1">
          {planTier === 'premium' ? `✨ ${planName} ✨` : planName}
        </h3>
        {isCurrent && (
          <Badge className="bg-white/10 text-white/60 border-white/20 text-[10px] md:text-xs">
            Atual
          </Badge>
        )}
      </div>

      {/* Top differentials - adaptativo com transição */}
      <div className={cn(
        "transition-all duration-300 ease-in-out overflow-hidden",
        visibleDifferentials.length > 0 && isExpanded 
          ? "max-h-[500px] opacity-100 mb-6" 
          : "max-h-0 opacity-0 mb-0"
      )}>
        <div className="relative space-y-3 bg-white/5 rounded-xl p-4 border border-white/10">
          {visibleDifferentials.map((diff, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-white/90 font-medium">{diff}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Price options - separado: mobile vs desktop */}
      <div className="mb-2 md:mb-4 w-full overflow-hidden">
        {/* MOBILE: Stack vertical com radio - 3 colunas com preços empilhados */}
        <div className="md:hidden space-y-1.5">
          {priceOptions.filter(opt => opt.price > 0).map(({ period, price, originalPrice, savings }) => {
            const isSelected = selectedOption?.planId === planId && selectedOption?.period === period;
            const isBestValue = period === 'quarterly' && savings && savings > 0;
            
            return (
              <button
                key={period}
                onClick={() => onSelectOption(planId, period)}
                className={cn(
                  "w-full grid grid-cols-[24px_minmax(80px,100px)_1fr] items-center gap-1 md:gap-2 p-2 rounded-lg border-2 transition-all duration-200",
                  isSelected && planTier === 'premium' && "border-luxury-pinkBright bg-luxury-pinkBright/10",
                  isSelected && planTier === 'standard' && "border-amber-500 bg-amber-500/10",
                  !isSelected && "border-white/20 bg-white/5"
                )}
              >
                {/* Radio */}
                <div className={cn(
                  "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                  isSelected && planTier === 'premium' && "border-luxury-pinkBright",
                  isSelected && planTier === 'standard' && "border-amber-500",
                  !isSelected && "border-white/30"
                )}>
                  {isSelected && (
                    <div className={cn(
                      "w-2.5 h-2.5 rounded-full",
                      planTier === 'premium' ? "bg-luxury-pinkBright" : "bg-amber-500"
                    )} />
                  )}
                </div>
                
                {/* Período */}
                <div className="text-[11px] md:text-xs font-bold text-white uppercase">
                  {PERIOD_LABELS[period].short}
                </div>
                
                {/* Preços empilhados: Original em cima, Final embaixo */}
                <div className="flex flex-col items-end gap-0.5">
                  {originalPrice && originalPrice > price && (
                    <div className="text-[10px] text-white/40 line-through">
                      R${originalPrice.toFixed(2)}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "font-bold text-sm",
                      isSelected ? "text-luxury-pinkBright" : "text-white"
                    )}>
                      R${price.toFixed(2)}
                    </span>
                    
                    {isBestValue && savings && savings > 0 && (
                      <span className="text-[8px] font-bold text-green-400 bg-green-500/20 px-1 py-0.5 rounded shrink-0">
                        -{savings}%
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* DESKTOP: Grid 3 colunas */}
        <div className={cn(
          "hidden md:grid gap-3",
          priceOptions.filter(opt => opt.price > 0).length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"
        )}>
          {priceOptions.filter(opt => opt.price > 0).map(({ period, price, originalPrice, savings }) => {
            const isSelected = selectedOption?.planId === planId && selectedOption?.period === period;
            const isBestValue = period === 'quarterly' && savings && savings > 0;
            
            return (
              <button
                key={period}
                onClick={() => onSelectOption(planId, period)}
                onMouseEnter={() => setHoveredPeriod(period)}
                onMouseLeave={() => setHoveredPeriod(null)}
              className={cn(
                "relative rounded-xl border-2 transition-all duration-200 flex flex-col items-stretch p-3 pt-5 min-h-[90px]",
                  "hover:brightness-110 active:brightness-95",
                  isSelected && planTier === 'premium' && "border-luxury-pinkBright bg-luxury-pinkBright/10 shadow-[0_0_15px_rgba(233,30,99,0.4)]",
                  isSelected && planTier === 'standard' && "border-amber-500 bg-amber-500/10 shadow-[0_0_15px_rgba(245,158,11,0.4)]",
                  !isSelected && "border-white/20 bg-white/5 hover:border-white/40"
                )}
              >
                <div className="text-xs font-bold text-white uppercase mb-1 text-center">
                  {PERIOD_LABELS[period].short}
                </div>
                
                {/* Preço original primeiro (riscado) */}
                {originalPrice && originalPrice > price && (
                  <div className="text-center text-xs text-white/40 line-through">
                    R${originalPrice.toFixed(2)}
                  </div>
                )}
                
                {/* Preço final (destaque) */}
                <div className="text-center flex items-center justify-center gap-1.5">
                  <span className={cn(
                    "font-bold text-lg",
                    isSelected && planTier === 'premium' && "text-luxury-pinkBright",
                    isSelected && planTier === 'standard' && "text-amber-500",
                    !isSelected && "text-white"
                  )}>
                    R${price.toFixed(2)}
                  </span>
                  {isBestValue && savings && savings > 0 && (
                    <span className="text-[9px] font-bold text-green-400 bg-green-500/20 px-1 py-0.5 rounded">
                      -{savings}%
                    </span>
                  )}
                </div>
                
                <div className="text-center text-[10px] text-white/50">
                  /{period === 'quarterly' ? 'trimestre' : period === 'weekly' ? 'semana' : 'mês'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Price per day display */}
      {densityMode !== 'compact' && pricePerDay && (
        <PricePerDayDisplay 
          monthlyPrice={pricePerDay * 30}
          className="mb-2"
          compact={densityMode === 'normal'}
        />
      )}
    </div>
  );
};